import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { getFileContentType } from '../lib/get-file-type';
import { loadScript } from '../lib/load-script';
import { PICFLOW_CONFIG } from '../picflow-config';
import { StorageService } from './storage.service';

export interface DropboxFile {
  name: string;
  path_lower: string;
  rev: string;
  size: number;
  source: 'dropbox';
}

@Injectable({
  providedIn: 'root',
})
export class DropboxService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dbx: any;
  private storage = inject(StorageService);
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  accessToken: string;

  private async loadDropbox() {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/dropbox.js/10.34.0/Dropbox-sdk.min.js');
    this.dbx = new window['Dropbox'].Dropbox({ accessToken: this.accessToken, clientId: 'd5tpqqbp7tfoqs8' });
  }

  public async authenticate() {
    await this.loadDropbox();

    const data = this.storage.get('dropbox-access-data');
    if (data && data.expires_at > Date.now()) {
      this.accessToken = data.access_token;
      return true;
    }

    this.storage.set('dropbox-redirect-return-path', location.pathname, true);
    location.href = await this.dbx.auth.getAuthenticationUrl(
      location.origin + '/a/dropbox-auth' //?redirect=' + encodeURIComponent(currentUrl)
    );
    return false;
  }

  public handleAuthResponse() {
    const returnUrl: string[] = (this.storage.get('dropbox-redirect-return-path') || '/a/galleries').split('/');
    const urlParams = new URLSearchParams(`?${location.hash.slice(1)}`);
    const access_token = urlParams.get('access_token');
    const expires_in = Number(urlParams.get('expires_in')) - 60; // 1 minute buffer
    if (!access_token) return returnUrl;
    const expires_at = Date.now() + expires_in * 1000;
    this.accessToken = access_token;
    this.storage.set('dropbox-access-data', { access_token, expires_at });
    return returnUrl;
  }

  public async listFiles(path = '') {
    if (!this.accessToken) {
      const authenticated = await this.authenticate();
      if (!authenticated) return;
    }
    await this.loadDropbox();
    try {
      const response = await this.dbx.filesListFolder({ path });
      if (!response?.result?.entries) return undefined;

      return response?.result?.entries.map(entry => ({
        ...entry,
        source: 'dropbox',
      }));
    } catch (err) {
      if (err.status === 401) {
        this.storage.remove('dropbox-access-data');
        return this.authenticate();
      }
      throw err;
    }
  }

  public async uploadFile(
    file: DropboxFile,
    galleryId: string,
    sectionId: string,
    position: number,
    assetToReplace?: AssetDto
  ): Promise<AssetDto> {
    return firstValueFrom(
      this.http.post<AssetDto>(this.config.internalApiUrlBase + '/upload-from-dropbox?with-auth=true', {
        gallery: galleryId,
        section: sectionId,
        asset_name: file.name,
        position,
        path: file.path_lower,
        rev: file.rev,
        asset_to_replace: assetToReplace?.id,
        dropboxToken: this.accessToken,
        content_type: getFileContentType(file),
      })
    );
  }
}
